import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileWarning,
  ExternalLink,
  Send,
  SquarePen,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  usePublishTargets,
  usePublishSource,
} from "@/hooks/useTechnicalAuditIssues";

interface PublishFirstNoticeProps {
  /** The unpublished issue — used to resolve WP/in-app publish targets. */
  issueId: string;
  /** Publish-first copy from the backend; falls back to a default. */
  message?: string | null;
  /** Runs before navigating away to the in-app editor (e.g. close the drawer). */
  onNavigate?: () => void;
  className?: string;
}

const DEFAULT_MESSAGE =
  "This page isn't published — it returns 404 to visitors, so there's nothing to " +
  "fix here yet. Publish the page first, then re-run the audit and the issue will " +
  "come back if it still applies.";

/**
 * Pre-apply banner for a source-page link fix whose page isn't publicly served.
 * Beyond the "publish first" message, it offers a CHOICE of how to resolve it —
 * publish the page in place, open the correct WordPress editor, or (when the page
 * maps to app-generated content) open the in-app editor. Targets are resolved
 * server-side on demand; only the actions that resolve are shown. Neutral coral
 * tone — action-needed, not a destructive error.
 */
export function PublishFirstNotice({
  issueId,
  message,
  onNavigate,
  className,
}: PublishFirstNoticeProps) {
  const navigate = useNavigate();
  const [confirmingPublish, setConfirmingPublish] = useState(false);
  const { data: targets, isLoading } = usePublishTargets(issueId, true);
  const publish = usePublishSource();

  const openInApp = () => {
    if (!targets?.in_app) return;
    onNavigate?.();
    navigate(targets.in_app.editor_url);
  };

  const canPublish = Boolean(targets?.can_publish);
  const wpEditUrl = targets?.wp_admin_edit_url ?? null;
  const inApp = targets?.in_app ?? null;
  const isPublishing = publish.isPending;
  // Resolution finished with nothing actionable (page likely deleted / WP not
  // connected). Surface the backend's reason so the user isn't left guessing.
  const noTargets =
    !isLoading && !canPublish && !wpEditUrl && !inApp;

  return (
    <div
      data-testid="publish-first-notice"
      className={`flex items-start gap-2 rounded-md border border-coral/30 bg-coral-light p-3 text-sm text-coral-dark ${className ?? ""}`}
    >
      <FileWarning className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1 space-y-2">
        <div>{message || DEFAULT_MESSAGE}</div>

        {isLoading && (
          <div className="flex items-center gap-1.5 text-xs text-coral-dark/80">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Checking WordPress for this page…
          </div>
        )}

        {noTargets && (
          <div className="text-xs text-coral-dark/80">
            {targets?.error ||
              "We couldn't locate this page in WordPress — it may have been deleted."}
          </div>
        )}

        {!isLoading && (canPublish || wpEditUrl || inApp) && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Publish in place — two-step confirm (live client-site change). */}
            {canPublish &&
              (confirmingPublish ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">Publish this page now?</span>
                  <Button
                    type="button"
                    size="sm"
                    disabled={isPublishing}
                    onClick={() =>
                      publish.mutate(issueId, {
                        onSettled: () => setConfirmingPublish(false),
                      })
                    }
                    className="gap-1.5 bg-coral text-white hover:bg-coral-hover"
                  >
                    {isPublishing ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5" />
                    )}
                    Yes, publish
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={isPublishing}
                    onClick={() => setConfirmingPublish(false)}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setConfirmingPublish(true)}
                  className="gap-1.5 bg-coral text-white hover:bg-coral-hover"
                >
                  <Send className="h-3.5 w-3.5" />
                  Publish now
                </Button>
              ))}

            {/* Open the correct WordPress editor (post id resolved server-side). */}
            {wpEditUrl && !confirmingPublish && (
              <Button asChild size="sm" variant="outline" className="gap-1.5">
                <a href={wpEditUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3.5 w-3.5" />
                  Edit in WordPress
                </a>
              </Button>
            )}

            {/* Open the in-app editor when the page is app-generated content. */}
            {inApp && !confirmingPublish && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={openInApp}
                className="gap-1.5"
              >
                <SquarePen className="h-3.5 w-3.5" />
                Open in app editor
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
